<template>
  <span class="cpu-load" :class="`_${level}`" v-tooltip="{ content: hint, triggers: ['hover', 'click'] }">
    <svg class="cpu-load__chart" :viewBox="`0 0 ${width} ${height}`" :width="width" :height="height" role="img" :aria-label="hint">
      <line class="cpu-load__base" x1="0" :y1="height - 0.5" :x2="width" :y2="height - 0.5" />
      <path class="cpu-load__area" :d="areaPath" />
      <polyline class="cpu-load__line" :points="linePoints" />
      <circle class="cpu-load__dot" :cx="lastPoint.x" :cy="lastPoint.y" r="2" />
    </svg>
    <span class="cpu-load__value">{{ format(current) }}%</span>
  </span>
</template>
<script>
// Host CPU load for the last 3 minutes as reported by the satellite: { interval_sec, samples }, percent, oldest first.
const WINDOW_SEC = 180;
const WARN_LEVEL = 70;
const HIGH_LEVEL = 90;

export default {
  name: "CpuLoadChart",
  props: {
    load: {
      type: Object,
      required: true,
    },
    width: {
      type: Number,
      default: 80,
    },
    height: {
      type: Number,
      default: 24,
    },
  },
  computed: {
    samples() {
      return this.load.samples || [];
    },
    current() {
      return this.samples.length ? this.samples[this.samples.length - 1] : 0;
    },
    average() {
      return this.samples.length ? this.samples.reduce((sum, value) => sum + value, 0) / this.samples.length : 0;
    },
    peak() {
      return this.samples.length ? Math.max(...this.samples) : 0;
    },
    level() {
      if (this.current >= HIGH_LEVEL) return "high";
      if (this.current >= WARN_LEVEL) return "warn";
      return "normal";
    },
    // The x axis always spans the full window and the y axis 0-100 %, so a fresh satellite fills the chart from the
    // right and an idle one stays near the bottom instead of being stretched.
    points() {
      const slots = Math.max(Math.round(WINDOW_SEC / (this.load.interval_sec || 1)), 2) - 1;
      const step = this.width / slots;
      const top = 2;
      const bottom = this.height - 1;
      const n = this.samples.length;
      const points = this.samples.map((value, i) => ({
        x: this.width - (n - 1 - i) * step,
        y: bottom - (Math.min(Math.max(value, 0), 100) / 100) * (bottom - top),
      }));
      // A single sample is drawn as a short flat segment.
      if (points.length === 1) points.unshift({ x: points[0].x - step, y: points[0].y });
      return points;
    },
    linePoints() {
      return this.points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    },
    areaPath() {
      const first = this.points[0];
      const last = this.points[this.points.length - 1];
      return `M${first.x.toFixed(1)},${this.height} L${this.linePoints.replace(/ /g, " L")} L${last.x.toFixed(1)},${this.height} Z`;
    },
    lastPoint() {
      return this.points[this.points.length - 1];
    },
    hint() {
      return this.$t("satellites.cpu.hint", {
        current: this.format(this.current),
        average: this.format(this.average),
        peak: this.format(this.peak),
      });
    },
  },
  methods: {
    format(value) {
      return value >= 10 ? Math.round(value).toString() : value.toFixed(1).replace(/\.0$/, "");
    },
  },
};
</script>
<style lang="scss" scoped>
$normal: #37807d;
$warn: #d48a00;
$high: #c62424;

.cpu-load {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: default;
  white-space: nowrap;

  &__chart {
    flex-shrink: 0;
    overflow: visible;
  }

  &__base {
    stroke: #e4e4ea;
    stroke-width: 1;
  }

  &__area {
    fill: currentColor;
    opacity: 0.15;
  }

  &__line {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.5;
    stroke-linejoin: round;
    stroke-linecap: round;
  }

  &__dot {
    fill: currentColor;
  }

  &__value {
    min-width: 34px;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  &._normal {
    color: $normal;
  }

  &._warn {
    color: $warn;
  }

  &._high {
    color: $high;
  }
}

body.dark .cpu-load {
  &__base {
    stroke: #3a3a41;
  }

  &._normal {
    color: #479495;
  }

  &._warn {
    color: #f0b44c;
  }

  &._high {
    color: #e36464;
  }
}
</style>
